import { faCalendarDays, faPrint } from '@fortawesome/pro-regular-svg-icons';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faListCheck } from '@fortawesome/pro-regular-svg-icons/faListCheck';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Option, Period } from '@spike/model';
import { TasksPendingCount } from '@spike/task-model';
import clsx from 'clsx';
import Filter from 'components/Bookings2/List/Filter/Filter';
import OrganizationMarketplacesFilter from 'components/Bookings2/List/OrganizationMarketplacesFilter';
import Button from 'components/UI/V3/Button/Button';
import SearchComponentPopup from 'components/UI/SearchComponentPopup';
import { DateRangePickerDropdown } from 'components/UI/V3/DateRangePickerDropdown';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Tabs } from 'components/UI/V2/Tabs/Tabs';
import { useTimeZone } from 'hooks';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';
import useIsMobile from 'hooks/useIsMobile';
import moment from 'moment-timezone';
import { ChangeEvent, FunctionComponent, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import RoomsDrawerFilter from '../Ops/filters/RoomsDrawerFilter';
import PrintModal from '../List/UI/Print/PrintModal/PrintModal';
import TasksTitlePortal from '../TasksTitlePortal';
import styles from './Dashboard.module.scss';

export type DatePeriod = Period;

interface TabModel {
    id: string;
    name: string;
}

export const STATUS_TABS: Array<TabModel> = [
    { id: 'upcoming', name: 'Upcoming' },
    { id: 'past_due', name: 'Past Due' },
    { id: 'completed', name: 'Completed' },
    { id: 'all', name: 'All' }
];

export const TASK_RANGE_OPTIONS: Array<Option<string>> = [
    { id: 'am', name: 'AM' },
    { id: 'pm', name: 'PM' },
    { id: 'morning', name: 'Morning' },
    { id: 'afternoon', name: 'Afternoon' },
    { id: 'evening', name: 'Evening' }
];

interface DashboardHeaderProps {
    search: string;
    category: string;
    selectedTabId: string;
    datePeriod: DatePeriod;
    selectedRoomIds: Array<number>;
    pendingCount?: TasksPendingCount;
    taskTypeOptions?: Array<Option<number>>;
    selectedTaskTypeIds?: Array<number>;
    selectedTaskRange?: string;
    selectedMarketplaceIds?: Array<number>;
    className?: string;
    onSearch: (search: string) => void;
    onChangeTab: (tabId: string) => void;
    onChangeDate: (period: DatePeriod) => void;
    onChangeRooms: (roomIds: Array<number>) => void;
    onChangeTaskTypes?: (taskTypeIds: Array<number>) => void;
    onChangeTaskRange?: (range: string | undefined) => void;
    onChangeMarketplaces?: (marketplaceIds: Array<number>) => void;
}

const tabCount = (tabId: string, counts?: TasksPendingCount): number => {
    if (!counts) {
        return 0;
    }
    switch (tabId) {
        case 'upcoming':
            return counts.upcomingCount;
        case 'past_due':
            return counts.pastDueCount;
        case 'completed':
            return counts.completedCount;
        case 'all':
            return counts.totalCount;
        default:
            return 0;
    }
};

export const DashboardHeader: FunctionComponent<DashboardHeaderProps> = props => {
    const [showPrint, setShowPrint] = useState(false);
    const [timesOpen, setTimesOpen] = useState(false);
    const debounced = useDebouncedCallback((value: string) => props.onSearch(value), 500);

    const selectedRangeLabel =
        TASK_RANGE_OPTIONS.find(option => option.id === props.selectedTaskRange)?.name ?? 'All Times';

    const selectRange = (range?: string) => {
        setTimesOpen(false);
        props.onChangeTaskRange?.(range);
    };

    const changeSearch = (event: ChangeEvent<HTMLInputElement>) => debounced(event.target.value);

    const isMobile = useIsMobile();
    const timeZone = useTimeZone();
    const { todayOption, tomorrowOption, next7DaysOption, thisMonthOption, last30DaysOption, lastMonthOption } =
        useDateRangeOptions();

    const allTimeOption = {
        label: 'All time',
        value: {
            from: moment.tz('2000-01-01', timeZone).startOf('day'),
            to: moment.tz(timeZone).add(2, 'years').endOf('day')
        }
    };

    const changePeriod = (period: Period | null) => period && props.onChangeDate(period);

    return (
        <div className={clsx(styles.header, props.className)}>
            {isMobile && (
                <TasksTitlePortal>
                    <SearchComponentPopup
                        flushIcon
                        title="Tasks"
                        placeholder="Search by clients or pets"
                        onSearch={text => debounced(text)}
                    />
                </TasksTitlePortal>
            )}
            <div className={styles.bar}>
                <div className={styles.left}>
                    <Input
                        value={props.search}
                        startIcon={<FontAwesomeIcon icon={faSearch} />}
                        placeholder="Search by clients or pets"
                        className={styles.search}
                        onChange={changeSearch}
                    />
                </div>

                <div className={styles.statusTabs}>
                    <Tabs
                        value={props.selectedTabId}
                        tabs={STATUS_TABS.map(tab => ({
                            id: tab.id,
                            name: tab.name,
                            count: tabCount(tab.id, props.pendingCount)
                        }))}
                        onChange={props.onChangeTab}
                    />
                </div>

                {props.taskTypeOptions && (
                    <Filter
                        className={styles.taskType}
                        showLabelOnMobile
                        allLabel="All Tasks"
                        quantityLabel="types"
                        icon={faListCheck}
                        options={props.taskTypeOptions}
                        selectedOptions={props.taskTypeOptions.filter(option =>
                            props.selectedTaskTypeIds?.includes(option.id)
                        )}
                        onChangeSelection={selected => props.onChangeTaskTypes?.(selected.map(option => option.id))}
                    />
                )}

                <div
                    className={styles.breakMobile}
                    aria-hidden
                />

                <div className={styles.datePicker}>
                    <DateRangePickerDropdown
                        timeZone={timeZone}
                        value={props.datePeriod}
                        onChange={changePeriod}
                        options={[
                            todayOption,
                            tomorrowOption,
                            next7DaysOption,
                            thisMonthOption,
                            last30DaysOption,
                            lastMonthOption,
                            allTimeOption
                        ]}
                        buttonProps={{
                            variant: 'outlined',
                            size: 'large',
                            leftIcon: faCalendarDays,
                            onlyIcon: isMobile,
                            className: styles.dateButton
                        }}
                    />
                </div>
                <OrganizationMarketplacesFilter
                    className={styles.locations}
                    selected={props.selectedMarketplaceIds ?? []}
                    onChangeSelection={props.onChangeMarketplaces}
                />
                <RoomsDrawerFilter
                    className={styles.rooms}
                    selected={props.selectedRoomIds}
                    onChangeSelection={props.onChangeRooms}
                />
                <div className={styles.times}>
                    <Dropdown
                        open={timesOpen}
                        onOpenChange={setTimesOpen}
                    >
                        <DropdownTrigger>
                            <Button
                                className={styles.timesButton}
                                variant="outlined"
                                size="large"
                                leftIcon={faClock}
                                label={selectedRangeLabel}
                            />
                            <div className={styles.timesMobileButton}>
                                <FontAwesomeIcon icon={faClock} />
                            </div>
                        </DropdownTrigger>
                        <DropdownContent
                            minWidth={160}
                            align="start"
                        >
                            <DropdownItem
                                className={styles.timesItem}
                                onSelect={() => selectRange(undefined)}
                            >
                                All Times
                            </DropdownItem>
                            <div className={styles.timesDivider} />
                            {TASK_RANGE_OPTIONS.map(option => (
                                <DropdownItem
                                    key={option.id}
                                    className={styles.timesItem}
                                    onSelect={() => selectRange(option.id)}
                                >
                                    {option.name}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
                <Button
                    className={styles.print}
                    label=""
                    onlyIcon
                    size="large"
                    leftIcon={faPrint}
                    variant="black"
                    onClick={() => setShowPrint(true)}
                />

                <div
                    className={styles.breakDesktop}
                    aria-hidden
                />
            </div>
            {showPrint && (
                <PrintModal
                    category={props.category}
                    selectedTab={props.selectedTabId}
                    initialRoomIds={props.selectedRoomIds}
                    onClose={() => setShowPrint(false)}
                />
            )}
        </div>
    );
};

export default DashboardHeader;
